import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Control, Controller, FieldErrors, UseFormSetValue, useWatch } from 'react-hook-form';
import { TextboxInput } from '../../atoms';
import type { ClientAccountEntry } from '../../../common/types/clients-entity';

export const formatAccountName = (typeVal: string, holderLabel: string): string => {
    const type = (typeVal ?? '').trim();
    const holder = (holderLabel ?? '').trim();
    if (type && holder) return `${holder}'s ${type}`;
    return holder || type || '';
};

interface AutoNameFieldProps {
    control: Control<ClientAccountEntry>;
    setValue: UseFormSetValue<ClientAccountEntry>;
    errors: FieldErrors<ClientAccountEntry>;
    holderLabelByValue: Record<string, string>;
    typeLabelByValue: Record<string, string>;
    placeholder: string;
    /** Externally-driven error message (e.g. duplicate-name check performed by the container). */
    externalError?: string;
    /** Fired whenever the user edits the field so the container can clear its external error. */
    onClearExternalError?: () => void;
}

// Account name field that auto-composes "<Holder>'s <Type>" from the selected type/holder
// labels while still allowing manual edits.
export const AutoNameField = ({
    control,
    setValue,
    errors,
    holderLabelByValue,
    typeLabelByValue,
    placeholder,
    externalError,
    onClearExternalError
}: AutoNameFieldProps) => {
    const { t } = useTranslation();
    const [typeVal, holderVal] = useWatch({ control, name: ['accountType', 'accountHolder'] });
    const didMountRef = useRef(false);

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        const typeLabel = typeLabelByValue[typeVal as string] ?? (typeVal as string) ?? '';
        const holderLabel = holderLabelByValue[holderVal as string] ?? (holderVal as string) ?? '';
        const composed = formatAccountName(typeLabel, holderLabel);
        setValue('accountName', composed, { shouldDirty: true, shouldValidate: true });
        onClearExternalError?.();
    }, [typeVal, holderVal, holderLabelByValue, typeLabelByValue, setValue]);

    const rhfMessage = errors.accountName?.message as string | undefined;
    const effectiveMessage = externalError || rhfMessage;
    const hasError = !!externalError || !!errors.accountName;
    if (externalError) {
        errors.accountName = {
            type: 'external',
            message: externalError
        };
    }
    return (
        <Controller
            name="accountName"
            control={control}
            rules={{
                required: {
                    value: true,
                    message: t('TEXT_ACCOUNT_NAME_REQUIRED_FIELD')
                },
                maxLength: {
                    value: 50,
                    message: t('TEXT_ACCOUNT_NAME_MAX_LENGTH')
                }
            }}
            render={({ field }) => (
                <TextboxInput
                    size="small"
                    placeholder={placeholder}
                    value={field.value ?? ''}
                    onChange={(e) => {
                        field.onChange(e);
                        onClearExternalError?.();
                    }}
                    error={hasError}
                    showTooltipError={true}
                    openTooltipError={hasError && Object.keys(errors).length === 1}
                    helperText={effectiveMessage}
                    fullWidth
                />
            )}
        />
    );
};
